import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { chatService } from '../services/chatService';
import api from '../services/api';

// Son de notification (préchargé)
const notificationSound = new Audio('/notification.wav');
notificationSound.volume = 0.5;

// Demander la permission pour les notifications navigateur
const requestNotificationPermission = () => {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
};

const showBrowserNotification = (senderName: string, messageText: string) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification('Nouveau message chat', {
      body: `${senderName}: ${messageText}`,
      icon: '/logo192.png',
      tag: 'chat-message',
    });
    // Fermer après 5 secondes
    setTimeout(() => notification.close(), 5000);
    // Clic sur la notification → focus sur le dashboard
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }
};

/**
 * Hook pour gérer le compteur de notifications de chat global
 * Utilisé pour afficher le badge sur l'icône Chat dans le menu
 */
export const useChatNotifications = (enabled: boolean = true) => {
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const location = useLocation();
  const isOnChatPage = location.pathname === '/chat';

  // Debug initial
  console.log('🔔 [useChatNotifications] Hook appelé, enabled:', enabled, 'isOnChatPage:', isOnChatPage);

  // Demander la permission notifications navigateur
  useEffect(() => {
    if (enabled) requestNotificationPermission();
  }, [enabled]);

  // Charger le nombre total de messages non lus
  const loadUnreadCount = useCallback(async () => {
    if (!enabled) {
      console.log('🔔 [useChatNotifications] Hook désactivé (enabled=false)');
      return;
    }
    
    console.log('🔔 [useChatNotifications] Chargement du compteur...');
    
    try {
      const response = await api.get('/chat/conversations');

      console.log('🔔 [useChatNotifications] Réponse API:', response.data);

      if (response.data?.success && Array.isArray(response.data.data)) {
        const total = response.data.data.reduce(
          (sum: number, conv: any) => sum + (parseInt(conv.unread_count, 10) || 0),
          0
        );
        console.log('🔔 [useChatNotifications] Total messages non lus:', total);
        setTotalUnreadCount(total);
      }
    } catch (error) {
      console.error('❌ [useChatNotifications] Erreur chargement compteur:', error);
    }
  }, [enabled]);

  // Écouter les nouveaux messages
  useEffect(() => {
    if (!enabled) {
      console.log('🔔 [useChatNotifications] useEffect: Hook désactivé');
      return;
    }
    
    console.log('🔔 [useChatNotifications] useEffect: Début initialisation');
    
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('currentUser');
    
    if (!token || !userStr) {
      console.log('🔔 [useChatNotifications] useEffect: Pas de token ou user');
      return;
    }

    try {
      const user = JSON.parse(userStr);
      console.log('🔔 [useChatNotifications] useEffect: User trouvé, ID:', user.id);

      // Charger le compteur initial directement
      console.log('🔔 [useChatNotifications] useEffect: Chargement du compteur initial...');
      (async () => {
        try {
          const response = await api.get('/chat/conversations');

          console.log('🔔 [useChatNotifications] Réponse conversations:', response.data);

          if (response.data?.success && Array.isArray(response.data.data)) {
            const total = response.data.data.reduce(
              (sum: number, conv: any) => sum + (parseInt(conv.unread_count, 10) || 0),
              0
            );
            console.log('🔔 [useChatNotifications] Total calculé:', total);
            setTotalUnreadCount(total);
          }
        } catch (error) {
          console.error('❌ [useChatNotifications] Erreur chargement conversations:', error);
        }
      })();

      // Écouter les nouveaux messages
      const handleNewMessage = (message: any) => {
        console.log('🔔 [useChatNotifications] Message reçu:', message);
        // Ne compter que les messages des clients (pas de l'admin)
        if (message.sender_role === 'customer') {
          // Jouer le son de notification
          notificationSound.currentTime = 0;
          notificationSound.play().catch(() => {});

          // Afficher une notification navigateur si pas sur la page chat
          if (!isOnChatPage) {
            const senderName = message.sender_name || 'Client';
            const text = message.content || message.message || 'Nouveau message';
            showBrowserNotification(senderName, text);

            console.log('🔔 [useChatNotifications] Incrémentation du compteur');
            setTotalUnreadCount(prev => {
              const newCount = prev + 1;
              console.log('🔔 [useChatNotifications] Nouveau compteur:', newCount);
              return newCount;
            });
          } else {
            console.log('🔔 [useChatNotifications] Sur la page chat, pas d\'incrémentation');
          }
        } else {
          console.log('🔔 [useChatNotifications] Message admin, ignoré');
        }
      };

      chatService.onMessage(handleNewMessage);

      // Écouter les messages lus pour recharger le compteur
      const handleMessagesRead = (data: { messageIds: number[] }) => {
        console.log('🔔 [useChatNotifications] Messages lus détectés:', data.messageIds.length);
        // Recharger le compteur depuis l'API
        loadUnreadCount();
      };

      chatService.onMessagesRead(handleMessagesRead);

      // Nettoyer à la déconnexion
      return () => {
        console.log('🔔 [useChatNotifications] useEffect: Nettoyage');
        chatService.offMessage(handleNewMessage);
        chatService.offMessagesRead(handleMessagesRead);
      };
    } catch (error) {
      console.error('❌ [useChatNotifications] Erreur parsing user:', error);
    }
  }, [enabled, isOnChatPage, loadUnreadCount]);

  // Recharger le compteur quand on quitte la page chat
  // Remettre à 0 quand on est sur la page chat (messages auto-marqués comme lus)
  useEffect(() => {
    if (isOnChatPage && enabled) {
      setTotalUnreadCount(0);
    } else if (!isOnChatPage && enabled) {
      loadUnreadCount();
    }
  }, [isOnChatPage, enabled, loadUnreadCount]);

  // Fonction pour recharger manuellement
  const refresh = useCallback(() => {
    if (enabled) {
      loadUnreadCount();
    }
  }, [loadUnreadCount, enabled]);

  return {
    totalUnreadCount,
    refresh
  };
};
